import { inject } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { AuthService, ThemeService, TokenRefreshService } from '@ld.consumer-portal/utils';

/**
 * Sends an already-valid session away from the login page before it renders.
 *
 * `LoginComponent.ngOnInit` also does this, but a component-level redirect runs after the
 * view is created, so the user saw the login page flash before landing on the dashboard.
 * The component check was keyed off `isAuthenticated()`, which only reports that a token
 * exists — a stale token bounced the user into `authGuard`, which then rejected them.
 *
 * Runs after `onboardingGuard` and `maintenanceGuard` in the route's `canActivate` array;
 * Angular resolves guard results by array index, so those keep precedence.
 *
 * JV theming routes signed-in users into origination via an async loan lookup, so that
 * case is deliberately left to the component.
 */
export const alreadyAuthenticatedGuard: CanActivateFn = async (): Promise<boolean | UrlTree> => {
  const authService = inject(AuthService);
  const themeService = inject(ThemeService);
  const tokenRefreshService = inject(TokenRefreshService);
  const router = inject(Router);

  if (!authService.isAuthenticated() || themeService.isJvThemeActive()) {
    return true;
  }

  if (!(await tokenRefreshService.ensureValidSession())) {
    authService.clearAllTokens();
    return true;
  }

  return router.createUrlTree(['/dashboard']);
};
